import PreviewLayout from "./preview-layout";
import type { Question } from "../form-builder/types";
import { DEFAULT_OPTION_VALUES } from "../form-builder/types";
import type { BrandKit } from "@/modules/brand/types";
import { useEditableField } from "./use-editable-field";

interface PreviewQuestionProps {
	id: string;
	question: Question;
	brandName: string;
	brandLogo: string | null;
	brandkit: BrandKit;
	questionIndex: number;
	totalQuestions: number;
	isSelected: boolean;
	onTitleChange: (title: string) => void;
	onDescriptionChange: (description: string) => void;
	onVideoOptionLabelChange: (value: string) => void;
	onVideoOptionDescriptionChange: (value: string) => void;
	onAudioOptionLabelChange: (value: string) => void;
	onAudioOptionDescriptionChange: (value: string) => void;
	onTextOptionLabelChange: (value: string) => void;
	onTextOptionDescriptionChange: (value: string) => void;
	isDarkMode?: boolean;
}

function EditableText({
	fieldRef,
	handlers,
	isEmpty,
	placeholder,
	textClassName,
	wrapperClassName,
	style,
	tag: Tag = "p",
	isDarkMode = false,
}: {
	fieldRef: ((el: HTMLElement | null) => void) | React.RefObject<HTMLElement | null>;
	handlers: ReturnType<typeof useEditableField>["handlers"];
	isEmpty: boolean;
	placeholder: string;
	textClassName?: string;
	wrapperClassName?: string;
	style?: React.CSSProperties;
	tag?: "p" | "span";
	isDarkMode?: boolean;
}) {
	return (
		<div className={`relative ${wrapperClassName || ""}`}>
			<Tag
				ref={fieldRef as React.Ref<HTMLParagraphElement>}
				className={`outline-none rounded-sm transition-shadow cursor-text ${
					isDarkMode
						? `hover:ring-2 hover:ring-gray-600 focus:ring-2 focus:ring-brand/50 ${isEmpty ? "text-transparent caret-white" : ""}`
						: `hover:ring-2 hover:ring-gray-300 focus:ring-2 focus:ring-brand/50 ${isEmpty ? "text-transparent caret-black" : ""}`
				} ${textClassName || ""}`}
				style={{ ...style, minHeight: "1.2em" }}
				contentEditable
				suppressContentEditableWarning
				{...handlers}
			/>
			{isEmpty && (
				<span className={`absolute inset-0 pointer-events-none opacity-40 ${isDarkMode ? "text-gray-500" : "text-gray-400"} ${textClassName || ""}`} style={style}>
					{placeholder}
				</span>
			)}
		</div>
	);
}

export default function PreviewQuestion({
	id,
	question,
	brandName,
	brandLogo,
	brandkit,
	questionIndex,
	totalQuestions,
	isSelected,
	onTitleChange,
	onDescriptionChange,
	onVideoOptionLabelChange,
	onVideoOptionDescriptionChange,
	onAudioOptionLabelChange,
	onAudioOptionDescriptionChange,
	onTextOptionLabelChange,
	onTextOptionDescriptionChange,
	isDarkMode = false,
}: PreviewQuestionProps) {
	void isSelected;

	const showFormatChoice = question.format === "any";
	const showQuestionCount = totalQuestions > 1;

	// Calculate progress - questions would be after rating step (step 1)
	const progressCurrent = questionIndex + 2; // +1 for rating, +1 for 1-based
	const progressTotal = totalQuestions + 5; // rating + questions + personal + consent + thankyou

	// Editable fields for option labels and descriptions
	const videoLabelField = useEditableField(question.video_option_label ?? DEFAULT_OPTION_VALUES.video_option_label, onVideoOptionLabelChange, DEFAULT_OPTION_VALUES.video_option_label);
	const videoDescField = useEditableField(
		question.video_option_description ?? DEFAULT_OPTION_VALUES.video_option_description,
		onVideoOptionDescriptionChange,
		DEFAULT_OPTION_VALUES.video_option_description,
	);
	const audioLabelField = useEditableField(question.audio_option_label ?? DEFAULT_OPTION_VALUES.audio_option_label, onAudioOptionLabelChange, DEFAULT_OPTION_VALUES.audio_option_label);
	const audioDescField = useEditableField(
		question.audio_option_description ?? DEFAULT_OPTION_VALUES.audio_option_description,
		onAudioOptionDescriptionChange,
		DEFAULT_OPTION_VALUES.audio_option_description,
	);
	const textLabelField = useEditableField(question.text_option_label ?? DEFAULT_OPTION_VALUES.text_option_label, onTextOptionLabelChange, DEFAULT_OPTION_VALUES.text_option_label);
	const textDescField = useEditableField(
		question.text_option_description ?? DEFAULT_OPTION_VALUES.text_option_description,
		onTextOptionDescriptionChange,
		DEFAULT_OPTION_VALUES.text_option_description,
	);

	return (
		<PreviewLayout
			id={id}
			brandName={brandName}
			brandLogo={brandLogo}
			brandkit={brandkit}
			eyebrow={showQuestionCount ? `Question ${questionIndex + 1} of ${totalQuestions}` : undefined}
			title={question.title}
			titlePlaceholder="Your question..."
			subtitle={question.description ?? ""}
			subtitlePlaceholder="Add a description..."
			showBackButton
			progress={{ current: progressCurrent, total: progressTotal }}
			centered={false}
			editableTitle
			editableSubtitle
			onLocalTitleChange={onTitleChange}
			onLocalSubtitleChange={onDescriptionChange}
			isDarkMode={isDarkMode}
		>
			{/* Format selection or input based on question format */}
			{showFormatChoice ? (
				<div className="space-y-3">
					{/* Video option - Recommended */}
					<div
						className={`w-full p-5 border-2 rounded-xl shadow-sm text-left flex items-start gap-4 relative ${isDarkMode ? "bg-gray-900" : "bg-white"}`}
						style={{
							borderColor: brandkit?.color_accent_hex || "#6366f1",
						}}
					>
						<span
							className="absolute top-0 right-4 -translate-y-1/2 px-2 py-0.5 text-xs font-medium rounded-full text-white flex items-center gap-1"
							style={{
								backgroundColor: brandkit?.color_accent_hex || "#6366f1",
								fontFamily: "var(--font-body, inherit)",
							}}
						>
							<svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
								<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
							</svg>
							Recommended
						</span>
						<svg className={`w-6 h-6 mt-0.5 shrink-0 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
							/>
						</svg>
						<div className="flex-1">
							<EditableText
								fieldRef={videoLabelField.ref}
								handlers={videoLabelField.handlers}
								isEmpty={videoLabelField.isEmpty}
								placeholder="Video option title..."
								textClassName={`font-medium ${isDarkMode ? "text-white" : "text-primary"}`}
								style={{ fontFamily: "var(--font-body, inherit)" }}
								isDarkMode={isDarkMode}
							/>
							<EditableText
								fieldRef={videoDescField.ref}
								handlers={videoDescField.handlers}
								isEmpty={videoDescField.isEmpty}
								placeholder="Video option description..."
								wrapperClassName="mt-1"
								textClassName={`text-sm ${isDarkMode ? "text-gray-400" : "text-secondary"}`}
								style={{ fontFamily: "var(--font-body, inherit)" }}
								isDarkMode={isDarkMode}
							/>
						</div>
					</div>

					{/* Audio option */}
					<div className={`w-full p-5 border rounded-xl shadow-sm text-left flex items-start gap-4 ${isDarkMode ? "border-gray-800 bg-gray-900" : "border-gray-100 bg-white"}`}>
						<svg className={`w-6 h-6 mt-0.5 shrink-0 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
							/>
						</svg>
						<div className="flex-1">
							<EditableText
								fieldRef={audioLabelField.ref}
								handlers={audioLabelField.handlers}
								isEmpty={audioLabelField.isEmpty}
								placeholder="Audio option title..."
								textClassName={`font-medium ${isDarkMode ? "text-white" : "text-primary"}`}
								style={{ fontFamily: "var(--font-body, inherit)" }}
								isDarkMode={isDarkMode}
							/>
							<EditableText
								fieldRef={audioDescField.ref}
								handlers={audioDescField.handlers}
								isEmpty={audioDescField.isEmpty}
								placeholder="Audio option description..."
								wrapperClassName="mt-1"
								textClassName={`text-sm ${isDarkMode ? "text-gray-400" : "text-secondary"}`}
								style={{ fontFamily: "var(--font-body, inherit)" }}
								isDarkMode={isDarkMode}
							/>
						</div>
					</div>

					{/* Text option */}
					<div className={`w-full p-5 border rounded-xl shadow-sm text-left flex items-start gap-4 ${isDarkMode ? "border-gray-800 bg-gray-900" : "border-gray-100 bg-white"}`}>
						<svg className={`w-6 h-6 mt-0.5 shrink-0 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
							/>
						</svg>
						<div className="flex-1">
							<EditableText
								fieldRef={textLabelField.ref}
								handlers={textLabelField.handlers}
								isEmpty={textLabelField.isEmpty}
								placeholder="Text option title..."
								textClassName={`font-medium ${isDarkMode ? "text-white" : "text-primary"}`}
								style={{ fontFamily: "var(--font-body, inherit)" }}
								isDarkMode={isDarkMode}
							/>
							<EditableText
								fieldRef={textDescField.ref}
								handlers={textDescField.handlers}
								isEmpty={textDescField.isEmpty}
								placeholder="Text option description..."
								wrapperClassName="mt-1"
								textClassName={`text-sm ${isDarkMode ? "text-gray-400" : "text-secondary"}`}
								style={{ fontFamily: "var(--font-body, inherit)" }}
								isDarkMode={isDarkMode}
							/>
						</div>
					</div>
				</div>
			) : question.format === "text" ? (
				// Text input preview - matching Textarea component styling from feedback-repo
				<div
					className={`w-full border rounded-lg shadow-sm py-3 px-5 text-base ${isDarkMode ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"}`}
					style={{ minHeight: "160px", fontFamily: "var(--font-body, inherit)" }}
				>
					<p className={`font-light text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
						Write a text testimonial
					</p>
				</div>
			) : question.format === "audio" ? (
				// Audio recorder preview
				<>
					<div className={`border rounded-xl p-8 text-center mb-4 ${isDarkMode ? "border-gray-800 bg-gray-900" : "border-gray-200 bg-white"}`}>
						<div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${isDarkMode ? "bg-gray-800" : "bg-gray-100"}`}>
							<svg className={`w-8 h-8 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
								/>
							</svg>
						</div>
					</div>
					<div className="text-center">
						<div className="px-4 py-2 text-sm font-medium rounded-lg transition-colors bg-white text-gray-700 border border-gray-200 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2">
							<svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 24 24">
								<circle cx="12" cy="12" r="10"></circle>
							</svg>
							Start Recording
						</div>
					</div>
				</>
			) : (
				// Video recorder preview
				<>
					<div className={`border rounded-xl p-8 text-center mb-4 ${isDarkMode ? "border-gray-800 bg-gray-900" : "border-gray-200 bg-white"}`}>
						<div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${isDarkMode ? "bg-gray-800" : "bg-gray-100"}`}>
							<svg className={`w-8 h-8 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
								/>
							</svg>
						</div>
					</div>
					<div className="text-center">
						<div className="px-4 py-2 text-sm font-medium rounded-lg transition-colors bg-white text-gray-700 border border-gray-200 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2">
							<svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 24 24">
								<circle cx="12" cy="12" r="10"></circle>
							</svg>
							Start Recording
						</div>
					</div>
				</>
			)}
		</PreviewLayout>
	);
}
